/*! **************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { Provider, defaultTheme } from '@adobe/react-spectrum'

import {
  A3dViewerElement,
  ModelInputAnimationStatus,
  ModelLoader,
} from '@a3d-viewer/viewer'

import Footer from '../components/Layout/Footer'
import Header from '../components/Layout/Header'
import MainContent from '../components/Layout/MainContent'
import useTheme from '../hooks/useTheme'
import { useEffect, useRef, useState } from 'react'
import DropZone from '../components/DropZone'
import { useAtomValue } from 'jotai'
import { viewerAtom } from '../config'
import useReadQueryParams from '../hooks/useReadQueryParams'

export type ModelType = {
  fileAsDataUrl: string
  extension?: ModelLoader
  file?: File
}

function MeshesAnimations() {
  const { theme } = useTheme()
  const viewerRef = useRef<A3dViewerElement>(null)
  const [model, setModel] = useState<ModelType | null>(null)
  const viewer = useAtomValue(viewerAtom)
  const { modelUrl } = useReadQueryParams()

  const handleDrop = ({ fileAsDataUrl, extension }: ModelType) => {
    setModel({ fileAsDataUrl, extension })
  }

  useEffect(() => {
    if (modelUrl) {
      setModel({ fileAsDataUrl: modelUrl })
    }
  }, [modelUrl])

  return (
    <Provider
      theme={defaultTheme}
      colorScheme={theme}
      id="provider"
      UNSAFE_className={`flex flex-1 flex-col ${theme === 'light' ? 'bg-white' : 'bg-black'}`}
      scale="medium"
    >
      <Header />
      <MainContent className="pr-4 pl-4 flex-col">
        <a3d-viewer ref={viewerRef}>
          <a3d-environment src={viewer.envSrc}></a3d-environment>

          <a3d-node position={[-1, 0, 0]}>
            <a3d-model
              scaling={[4, 4, 4]}
              src={
                'https://assets.babylonjs.com/meshes/Demos/optimized/acrobaticPlane_variants.glb'
              }
              animationStatus={ModelInputAnimationStatus.PLAY}
              selectedAnimation={0}
            ></a3d-model>
          </a3d-node>
          <a3d-model
            src={
              'https://raw.githubusercontent.com/BabylonJS/Assets/master/meshes/ufo.glb'
            }
            animationStatus={ModelInputAnimationStatus.PLAY}
            selectedAnimation={2}
          ></a3d-model>

          <a3d-loading-screen></a3d-loading-screen>
        </a3d-viewer>
      </MainContent>
      <Footer id="footer" className="flex justify-center w-full">
        Adobe 2025
      </Footer>
    </Provider>
  )
}

export default MeshesAnimations
